/* Клиент API этапа 4 «Входящие»: каналы (WhatsApp через шлюз), диалоги, сообщения, живые события.
   Права решает сервер: агент видит свои, неназначенные и те диалоги, где писал; каналы подключают
   владелец, администраторы и сотрудники с правом «Каналы». */
import { api, apiUrl } from "@/lib/api";
import type { ZohoCustomer } from "./companies-api";
import { locale, t } from "@/lib/i18n";

export type ChannelStatus = "new" | "qr" | "pairing" | "connecting" | "connected" | "unauthorized" | "disconnected" | "failed" | "archive";

/** живое состояние сессии шлюза — приходит только тем, кто может подключать каналы */
export type GatewayState = {
  state: "starting" | "qr" | "pairing" | "password" | "connecting" | "open" | "closed" | "logged_out" | "failed" | "gateway_down" | "unknown";
  qr?: string | null;
  pairingCode?: string | null;
  phone?: string | null;
  name?: string | null;
  error?: string | null;
};

export type Channel = {
  id: string;
  kind: "whatsapp" | "telegram" | string;
  name: string;
  phone: string | null;
  accountName: string;
  status: ChannelStatus;
  statusAt: string;
  statusError: string | null;
  /** botName — имя бота, который отвечает с самого телефона (диалоги без ответственного «ведёт бот», продавец перехватывает) */
  settings: {
    historyDays: number;
    readReceipts: boolean;
    autoContact: boolean;
    botName: string;
    defaultAssigneeId: string | null;
    teamId: string | null;
    /** почта: сотрудник-владелец ящика, закончена ли первая загрузка, последняя синхронизация (unix) и ошибка */
    userId?: string | null;
    backfilled?: boolean;
    lastSyncAt?: number | null;
    lastError?: string | null;
  };
  createdBy: string | null;
  createdAt: string;
  state: GatewayState | null;
};

export type MessageKind = "in" | "out" | "note" | "system";
export type MessageStatus = "pending" | "sent" | "delivered" | "read" | "failed";

export type Media = {
  kind: "image" | "video" | "audio" | "document" | "sticker" | "location" | "contact" | string;
  mime?: string;
  name?: string | null;
  size?: number | null;
  duration?: number | null;
  width?: number | null;
  height?: number | null;
  ptt?: boolean;
  /** файл есть на сервере — ссылка для <img>/<audio>/скачивания; null — вложение без файла (истёкшая история) */
  url: string | null;
  skipped?: string;
  lat?: number;
  lng?: number;
};

export type Message = {
  id: number;
  conversationId: string;
  externalId: string | null;
  kind: MessageKind;
  text: string;
  media: Media | null;
  quotedExternalId: string | null;
  authorId: string | null;
  author: string | null;
  fromMe: boolean;
  /** написано с самого телефона, а не из Hub */
  fromPhone: boolean;
  status: MessageStatus;
  error: string | null;
  history: boolean;
  /** системные отметки: { sys: "take" | "handoff" | "unassign", actor, target } — чтобы показать на языке интерфейса */
  meta: Record<string, unknown> | null;
  sentAt: string;
  createdAt: string;
};

export type Conversation = {
  id: string;
  channelId: string | null;
  channel: string;
  channelName: string;
  archive: boolean;
  externalId: string;
  phone: string | null;
  peerName: string;
  /** имя для показа: контакт, иначе имя профиля WhatsApp, иначе номер */
  name: string;
  contactId: string | null;
  company: string;
  stageId: string | null;
  assigneeId: string | null;
  assignee: string | null;
  /** имя бота канала, пока у диалога нет ответственного */
  bot: string | null;
  teamId: string | null;
  members: string[];
  status: "open" | "closed" | "snoozed";
  category: string;
  /** итог и тональность от ИИ после закрытия */
  summary: string;
  sentiment: "positive" | "neutral" | "negative" | "";
  /** продавец из Zoho (Sales Person сделки/контакта/клиента Books) — по нему распределяются диалоги */
  salesperson: string;
  unread: number;
  waiting: boolean;
  lastMessageAt: string | null;
  lastMessageText: string;
  lastMessageKind: "in" | "out" | "";
  firstInAt: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

/** оценка диалога в базе знаний (baza.starec.ai): статус передачи, баллы, этап, ссылка */
export type BazaInfo = {
  status: "pending" | "sent" | "skipped" | "error";
  error: string | null;
  url: string | null;
  analysisStatus: "pending" | "running" | "done" | "failed" | null;
  score: number | null;
  stage: string | null;
  summary: string | null;
  syncedAt: string | null;
  checkedAt: string | null;
};

export type ConversationFull = Conversation & {
  messages: Message[];
  hasMore: boolean;
  baza: BazaInfo | null;
  contact: { id: string; name: string; phone: string | null; email: string | null; company: string; companyId: string | null; stageId: string | null; ownerId: string | null } | null;
};

export type Folders = {
  all: number;
  mine: number;
  unassigned: number;
  bot: number;
  collaborations: number;
  waiting: number;
  unread: number;
  closed: number;
  linked: number;
  unlinked: number;
  company: number;
  assignees: Record<string, number>;
  stages: Record<string, number>;
  teams: Record<string, number>;
};

export type ConversationQuery = Partial<{
  folder: string;
  q: string;
  sort: "new" | "old" | "unread";
  waiting: boolean;
  channel: string;
  salesperson: string;
  assignee: string;
  archive: boolean;
  limit: number;
  offset: number;
}>;

/** Лимит файла в диалоге (как MAX_FILE на сервере); в WhatsApp фото/видео/аудио крупнее 16 МБ уходят документом */
export const MAX_UPLOAD_MB = 100;
export const WA_MEDIA_MB = 16;

/** Режим «С архивом» во «Входящих»: закрытые диалоги (архив Wazzup, старая почта) тоже в списке и в счётчиках — на плитках
    главного экрана и в «Живом чате» то же самое. Запоминается в браузере; по умолчанию у сотрудников включён, у агентов выключен. */
const ARCHIVE_KEY = "hub.inbox.archive";
export const inboxArchive = (role?: string | null): boolean => {
  const v = typeof localStorage === "undefined" ? null : localStorage.getItem(ARCHIVE_KEY);
  return v === null ? role !== "agent" : v === "1";
};
export const setInboxArchive = (v: boolean) => localStorage.setItem(ARCHIVE_KEY, v ? "1" : "0");

const qs = (o: Record<string, unknown>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v !== undefined && v !== null && v !== "" && v !== false) p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : "";
};

export type TelegramApp = { gateway: boolean; configured: boolean; source: "env" | "app" | "none"; apiId: number | null; canEdit: boolean };

export type EmailApp = { configured: boolean; domains: string[]; users: { id: string; name: string; email: string }[]; historyDays: number; canEdit: boolean };

/** Письмо в сообщении диалога почтового канала (meta.email) */
export type EmailMeta = {
  subject: string;
  from: string;
  fromName: string;
  to: string[];
  cc: string[];
  threadId: string | null;
  gmailId: string;
  attachments: { name: string; mime: string; size: number; id: string }[];
};
export const emailOf = (m: Message): EmailMeta | null => ((m.meta as { email?: EmailMeta } | null)?.email as EmailMeta | undefined) ?? null;

export const channelsApi = {
  list: () => api<{ items: Channel[]; gateway: boolean; telegram: boolean; mail: boolean; canManage: boolean }>("/channels"),
  /** Почта: готов ли сервисный аккаунт Google, свои домены, сотрудники без подключённого ящика */
  emailApp: () => api<EmailApp>("/channels/email/app"),
  /** Ключи приложения Telegram (api_id/api_hash с my.telegram.org) — один раз для всей компании, вводятся в мастере подключения */
  telegramApp: () => api<TelegramApp>("/channels/telegram/app"),
  setTelegramApp: (apiId: number, apiHash: string) => api<TelegramApp>("/channels/telegram/app", { method: "PUT", json: { apiId, apiHash } }),
  get: (id: string) => api<Channel>(`/channels/${id}`),
  create: (kind: "whatsapp" | "telegram" | "email" = "whatsapp", name = "") => api<Channel>("/channels", { method: "POST", json: { kind, name } }),
  pair: (id: string, phone: string) => api<Channel>(`/channels/${id}/pair`, { method: "POST", json: { phone } }),
  /** Telegram: облачный пароль двухэтапной проверки после сканирования QR (Hub его не хранит) */
  password: (id: string, password: string) => api<Channel>(`/channels/${id}/password`, { method: "POST", json: { password } }),
  reconnect: (id: string) => api<Channel>(`/channels/${id}/reconnect`, { method: "POST" }),
  logout: (id: string) => api<Channel>(`/channels/${id}/logout`, { method: "POST" }),
  update: (id: string, body: Partial<Channel["settings"]> & { name?: string }) => api<Channel>(`/channels/${id}`, { method: "PATCH", json: body }),
  remove: (id: string) => api(`/channels/${id}`, { method: "DELETE" }),
};

export const conversationsApi = {
  folders: (archive = false) => api<Folders>(`/conversations/folders${qs({ archive })}`),
  list: (q: ConversationQuery = {}) => api<{ items: Conversation[]; total: number }>(`/conversations${qs(q)}`),
  get: (id: string, before?: number) => api<ConversationFull>(`/conversations/${id}${qs({ before })}`),
  /** передать диалог в базу знаний на оценку сейчас, не дожидаясь расписания */
  sendToBaza: (id: string) => api<{ baza: BazaInfo | null }>(`/conversations/${id}/baza`, { method: "POST" }),
  /** написать первым: WhatsApp — по телефону, почта — по адресу (email) */
  create: (body: { channelId: string; phone?: string; email?: string; contactId?: string }) => api<Conversation>("/conversations", { method: "POST", json: body }),
  update: (id: string, body: Partial<{ assigneeId: string | null; teamId: string | null; status: Conversation["status"]; category: string; contactId: string | null; note: string }>) =>
    api<Conversation>(`/conversations/${id}`, { method: "PATCH", json: body }),
  read: (id: string) => api(`/conversations/${id}/read`, { method: "POST" }),
  /** «Переслать»: текст и/или файл сообщения в другие диалоги (можно в другой канал) */
  forward: (id: string, messageId: number, to: string[]) => api<{ items: Message[] }>(`/conversations/${id}/forward`, { method: "POST", json: { messageId, to } }),
  send: (id: string, text: string, kind: "out" | "note" = "out", replyTo?: string | null, snippetId?: string | null) =>
    api<Message & { also?: Message[] }>(`/conversations/${id}/messages`, { method: "POST", json: { text, kind, replyTo: replyTo ?? null, snippetId: snippetId ?? null } }),
  /** клиенты Zoho Books в диалоге: связанные с контактом и подсказки по совпавшему телефону */
  books: (id: string) => api<{ linked: ZohoCustomer[]; suggested: ZohoCustomer[]; canLink: boolean }>(`/conversations/${id}/books`),
  upload: (id: string, file: File, caption = "") => {
    const fd = new FormData();
    fd.append("file", file, file.name);
    fd.append("caption", caption);
    return api<Message>(`/conversations/${id}/upload`, { method: "POST", body: fd });
  },
};

export const fileUrl = (m: Message) => (m.media?.url ? `${apiUrl(m.media.url.replace(/^\/api/, ""))}` : null);

export const CHANNEL_STATUS_LABEL: Record<ChannelStatus, string> = {
  new: t("Создан"),
  qr: t("Ждёт сканирования QR-кода"),
  pairing: t("Ждёт код на телефоне"),
  connecting: t("Подключается…"),
  connected: t("Подключён"),
  unauthorized: t("Не авторизовано"),
  disconnected: t("Отключён"),
  failed: t("Ошибка подключения"),
  archive: t("Архив"),
};

export const hm = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }) : "");

/** время для списка диалогов: сегодня — ЧЧ:ММ, вчера — «Вчера», дальше — дата */
export function listTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return hm(iso);
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return t("Вчера");
  return d.toLocaleDateString(locale, { day: "numeric", month: "short" });
}

/** разделитель дней в ленте сообщений: «Сегодня», «Вчера», дальше — «12 октября» (с годом, если не текущий) */
export function dayLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return t("Сегодня");
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return t("Вчера");
  return d.toLocaleDateString(locale, { day: "numeric", month: "long", year: d.getFullYear() === now.getFullYear() ? undefined : "numeric" });
}

/** «пришли из другого раздела» — открыть диалог во «Входящих» (например, из карточки контакта или уведомления) */
let pendingConversation: string | null = null;
export const showConversation = (id: string) => {
  pendingConversation = id;
};
export const takeConversation = () => {
  const id = pendingConversation;
  pendingConversation = null;
  return id;
};

/** «Написать в WhatsApp» / «Написать письмо» из карточки клиента: открыть «Входящие» с окном нового диалога и подставленным адресатом */
let pendingNewPhone: string | null = null;
/** куда вернуться, если окно закрыли без отправки (карточка контакта/компании, из которой пришли) */
let pendingNewBack: (() => void) | null = null;
/** открыть «Новый диалог» с заполненным адресатом: телефон (WhatsApp) или адрес почты; back — вернуться на прежний экран при отмене */
export const showNewDialog = (to: string, back?: () => void) => {
  pendingNewPhone = to;
  pendingNewBack = back ?? null;
};
export const takeNewDialog = () => {
  const v = pendingNewPhone;
  pendingNewPhone = null;
  return v;
};
export const takeNewDialogBack = () => {
  const f = pendingNewBack;
  pendingNewBack = null;
  return f;
};
